import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Send, RotateCcw, Sparkles, Menu, Plus, Trash2, Pencil, Check } from 'lucide-react';
import axios from 'axios';
import { buildSprintContextSnapshot } from '../lib/sprintMetrics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIVE_SESSION_KEY = 'tactixai_active_session_id';

const getUserId = () => {
  let userId = localStorage.getItem('timetactix_user_id');
  if (!userId) {
    userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('timetactix_user_id', userId);
  }
  return userId;
};

const newSessionId = () =>
  `tactixai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getInitialSessionId = () => {
  let sid = localStorage.getItem(ACTIVE_SESSION_KEY);
  if (!sid) {
    sid = newSessionId();
    localStorage.setItem(ACTIVE_SESSION_KEY, sid);
  }
  return sid;
};

const formatMsgTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  try {
    const date = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(d);
    const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
    return `${date} · ${time}`;
  } catch (_) {
    return '';
  }
};

const getBrowserTZ = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    return '';
  }
};

const QUICK_PROMPTS = [
  'Plan my Sprint for tonight',
  "I'm tired after work but want to make progress",
  'How do I structure my evenings for my side hustle?',
  "How do I stay consistent when I'm not motivated?",
];

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'if', 'so', 'to', 'of', 'in', 'on',
  'for', 'with', 'my', 'me', 'i', 'im', 'is', 'it', 'be', 'do', 'you', 'your',
  'this', 'that', 'have', 'has', 'about', 'at', 'can', 'want', 'need',
]);

const deriveTitle = (message) => {
  if (!message || typeof message !== 'string') return 'New chat';
  const clean = message.replace(/[\r\n]+/g, ' ').trim();
  if (!clean) return 'New chat';
  const words = clean.split(/\s+/);
  // Prefer up to ~6 words, preserving order, but skip leading stop words for punch
  let start = 0;
  while (start < words.length && STOP_WORDS.has(words[start].toLowerCase())) {
    start += 1;
  }
  const picked = words.slice(start, start + 6);
  let title = picked.join(' ').replace(/[.?!,;:]+$/, '');
  // Capitalise first letter
  if (title.length > 0) title = title[0].toUpperCase() + title.slice(1);
  if (words.length > start + 6) title += '…';
  if (title.length > 60) title = title.slice(0, 57) + '…';
  return title || 'New chat';
};

// Sprint Card contract from TactixAI system prompt
const ALLOWED_DURATIONS = [30, 60, 90, 120];

const parseSprintCard = (text) => {
  if (!text || typeof text !== 'string') return null;
  const goalMatch = text.match(/\*\*Sprint Goal:\*\*\s*\n?\s*([^\n]+(?:\n(?!\s*\*\*)[^\n]*)*)/i);
  const durationMatch = text.match(/\*\*Recommended Duration:\*\*\s*\n?\s*([^\n]+)/i);
  if (!goalMatch || !durationMatch) return null;

  const goal = goalMatch[1].trim();
  const durationRaw = durationMatch[1].trim();
  const durationNumMatch = durationRaw.match(/(30|60|90|120)/);
  if (!durationNumMatch) return null;
  const duration = parseInt(durationNumMatch[1], 10);
  if (!ALLOWED_DURATIONS.includes(duration)) return null;
  if (!goal) return null;

  let breaks = null;
  let breaksReason = null;
  if (duration === 60 || duration === 120) {
    const breaksMatch = text.match(/\*\*Breaks:\*\*\s*\n?\s*(ON|OFF)\b(?:\s*[—-]\s*([^\n]+))?/i);
    if (!breaksMatch) return null;
    breaks = breaksMatch[1].toUpperCase();
    breaksReason = (breaksMatch[2] || '').trim() || null;
  }

  const beforeMatch = text.match(/\*\*Before You Start:\*\*\s*\n([\s\S]+?)(?:\n\s*\*\*|$)/i);
  let beforeItems = [];
  if (beforeMatch) {
    beforeItems = beforeMatch[1]
      .split('\n')
      .map((l) => l.replace(/^\s*[-*•]\s*/, '').trim())
      .filter(Boolean);
  }

  return { goal, duration, breaks, breaksReason, beforeItems };
};

// Lightweight, safe assistant-message renderer.
// Supports ONLY: **bold**, bullet lists (- / *), paragraphs (blank line),
// and natural line breaks. Never renders raw HTML.
const renderInline = (text, keyPrefix = '') => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*\n][^*\n]*?\*\*)/g);
  return parts.map((chunk, i) => {
    if (chunk.startsWith('**') && chunk.endsWith('**') && chunk.length >= 4) {
      return (
        <strong key={`${keyPrefix}b${i}`} className="font-semibold text-white">
          {chunk.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}t${i}`}>{chunk}</React.Fragment>;
  });
};

const MarkdownLite = ({ text }) => {
  if (!text) return null;
  // Split into paragraph blocks by blank line
  const blocks = String(text).replace(/\r\n/g, '\n').split(/\n{2,}/);
  return (
    <div className="space-y-2">
      {blocks.map((block, bi) => {
        // Within a block, group consecutive bullet lines into <ul> segments and
        // consecutive non-bullet lines into a <p> segment so bullets that follow
        // a bold heading with only a single newline still render cleanly.
        const lines = block.split('\n');
        const segments = [];
        let current = null;
        lines.forEach((line) => {
          const isBullet = /^\s*[-*]\s+/.test(line);
          const type = isBullet ? 'ul' : 'p';
          if (!current || current.type !== type) {
            current = { type, lines: [line] };
            segments.push(current);
          } else {
            current.lines.push(line);
          }
        });
        return (
          <div key={`blk-${bi}`} className="space-y-1.5">
            {segments.map((seg, si) => {
              if (seg.type === 'ul') {
                return (
                  <ul
                    key={`ul-${bi}-${si}`}
                    className="list-disc list-outside pl-5 space-y-1 marker:text-slate-400"
                  >
                    {seg.lines.map((line, li) => (
                      <li key={`li-${bi}-${si}-${li}`} className="leading-relaxed">
                        {renderInline(line.replace(/^\s*[-*]\s+/, ''), `${bi}-${si}-${li}-`)}
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p key={`p-${bi}-${si}`} className="leading-relaxed">
                  {seg.lines.map((line, li) => (
                    <React.Fragment key={`ln-${bi}-${si}-${li}`}>
                      {li > 0 && <br />}
                      {renderInline(line, `${bi}-${si}-${li}-`)}
                    </React.Fragment>
                  ))}
                </p>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

const SprintCard = ({ card, onUseSprint }) => {
  const durationLabel = `${card.duration} minutes`;
  return (
    <div
      data-testid="tactixai-sprint-card"
      data-duration={card.duration}
      data-breaks={card.breaks || 'na'}
      className="w-full max-w-full rounded-lg border border-amber-500/40 bg-slate-900/70 shadow-inner overflow-hidden"
    >
      <div className="px-3 py-1.5 border-b border-amber-500/30 bg-gradient-to-r from-amber-500/15 to-orange-500/10">
        <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-amber-300">
          Sprint Card
        </div>
      </div>
      <div className="px-3 py-3 space-y-3 text-sm text-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
            Sprint Goal
          </div>
          <div className="text-sm leading-snug break-words" data-testid="tactixai-card-goal">
            {card.goal}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
            Recommended Duration
          </div>
          <div className="text-sm" data-testid="tactixai-card-duration">
            {durationLabel}
          </div>
        </div>
        {card.breaks && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">
              Breaks
            </div>
            <div className="text-sm break-words" data-testid="tactixai-card-breaks">
              <span
                className={
                  card.breaks === 'ON'
                    ? 'text-green-400 font-medium'
                    : 'text-slate-300 font-medium'
                }
              >
                {card.breaks}
              </span>
              {card.breaksReason && (
                <span className="text-slate-300"> — {card.breaksReason}</span>
              )}
            </div>
          </div>
        )}
        {card.beforeItems && card.beforeItems.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
              Before You Start
            </div>
            <ul className="space-y-1 text-sm text-slate-200">
              {card.beforeItems.map((item, i) => (
                <li key={i} className="flex gap-2 leading-snug">
                  <span className="text-amber-400 shrink-0">•</span>
                  <span className="break-words">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <button
          type="button"
          onClick={() => onUseSprint(card)}
          data-testid="tactixai-use-sprint-button"
          className="w-full inline-flex items-center justify-center rounded-md bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium py-2 hover:from-amber-600 hover:to-orange-600 transition-colors"
        >
          Use this Sprint
        </button>
      </div>
    </div>
  );
};

const ConversationRow = ({ conv, isActive, onSelect, onRequestDelete, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(conv.title || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(conv.title || '');
      // focus + select on next tick
      const t = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 0);
      return () => clearTimeout(t);
    }
  }, [isEditing, conv.title]);

  const commit = () => {
    const trimmed = (draft || '').trim();
    if (!trimmed || trimmed === conv.title) {
      setIsEditing(false);
      return;
    }
    onRename(conv, trimmed);
    setIsEditing(false);
  };

  const cancel = () => setIsEditing(false);

  if (isEditing) {
    return (
      <div
        data-testid={`tactixai-conv-row-${conv.session_id}`}
        data-editing="true"
        className="flex items-center gap-1 rounded-md px-2 py-1.5 border border-amber-500/40 bg-slate-900"
      >
        <input
          ref={inputRef}
          value={draft}
          maxLength={80}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`tactixai-conv-rename-input-${conv.session_id}`}
          className="flex-1 min-w-0 bg-slate-950/70 border border-slate-700 rounded px-1.5 py-1 text-xs text-white focus:outline-none focus:border-amber-500/70"
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            commit();
          }}
          data-testid={`tactixai-conv-rename-save-${conv.session_id}`}
          aria-label="Save title"
          className="p-1 rounded text-amber-300 hover:text-amber-200 hover:bg-slate-800"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            cancel();
          }}
          data-testid={`tactixai-conv-rename-cancel-${conv.session_id}`}
          aria-label="Cancel rename"
          className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid={`tactixai-conv-row-${conv.session_id}`}
      data-active={isActive ? 'true' : 'false'}
      className={`group flex items-center gap-1 rounded-md px-2 py-1.5 cursor-pointer transition-colors ${
        isActive
          ? 'bg-amber-500/15 border border-amber-500/40'
          : 'hover:bg-slate-800/70 border border-transparent'
      }`}
      onClick={() => onSelect(conv.session_id)}
      role="button"
      tabIndex={0}
    >
      <div
        className={`flex-1 min-w-0 text-xs truncate ${
          isActive ? 'text-amber-100 font-medium' : 'text-slate-300'
        }`}
        title={conv.title}
      >
        {conv.title || 'New chat'}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditing(true);
        }}
        data-testid={`tactixai-conv-edit-${conv.session_id}`}
        aria-label={`Rename ${conv.title}`}
        className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-amber-300 hover:bg-slate-700/60 transition-opacity"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRequestDelete(conv);
        }}
        data-testid={`tactixai-conv-delete-${conv.session_id}`}
        aria-label={`Delete ${conv.title}`}
        className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-red-400 hover:bg-slate-700/60 transition-opacity"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const TactixAIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(getInitialSessionId);
  const [conversations, setConversations] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingReset, setPendingReset] = useState(false);
  const [usage, setUsage] = useState({ used: 0, limit: 15, remaining: 15 });
  const [limitError, setLimitError] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const closeTimerRef = useRef(null);
  // Track which sessions have had a title derived from a user message already
  const titledSessionsRef = useRef(new Set());
  // Track sessions that have been manually renamed to prevent auto-title override
  const manualTitledSessionsRef = useRef(new Set());

  const persistActiveSession = (sid) => {
    localStorage.setItem(ACTIVE_SESSION_KEY, sid);
  };

  // Open/close animation orchestration (respects prefers-reduced-motion)
  const openPanel = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsMounted(true);
    setIsOpen(true);
    // Enter next frame so CSS transition triggers from the initial closed state
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsEntering(true));
    });
  };

  const closePanel = () => {
    setIsEntering(false);
    setIsOpen(false);
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = prefersReduced ? 0 : 220;
    closeTimerRef.current = setTimeout(() => {
      setIsMounted(false);
      closeTimerRef.current = null;
    }, delay);
  };

  const refreshConversations = useCallback(async () => {
    const userId = getUserId();
    try {
      const res = await axios.get(
        `${API}/tactixai/conversations?user_id=${encodeURIComponent(userId)}`
      );
      const list = res.data?.conversations || [];
      setConversations(list);
    } catch (_) {
      // best-effort
    }
  }, []);

  const refreshUsage = useCallback(async () => {
    const userId = getUserId();
    const tz = getBrowserTZ();
    try {
      const res = await axios.get(
        `${API}/tactixai/usage?user_id=${encodeURIComponent(userId)}${tz ? `&tz=${encodeURIComponent(tz)}` : ''}`
      );
      if (res.data) setUsage(res.data);
    } catch (_) {
      // best-effort
    }
  }, []);

  const loadHistory = useCallback(async (sid) => {
    const userId = getUserId();
    try {
      const res = await axios.get(
        `${API}/tactixai/history/${sid}?user_id=${encodeURIComponent(userId)}`
      );
      const loaded = (res.data?.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        created_at: m.created_at,
      }));
      setMessages(loaded);
      // If this session already had user messages, mark it as titled so we don't rename
      if (loaded.some((m) => m.role === 'user')) {
        titledSessionsRef.current.add(sid);
      }
    } catch (_) {
      setMessages([]);
    }
  }, []);

  // Initial load whenever panel opens
  useEffect(() => {
    if (!isOpen) return;
    refreshConversations();
    refreshUsage();
    loadHistory(activeSessionId);
  }, [isOpen, activeSessionId, refreshConversations, refreshUsage, loadHistory]);

  // Listen for pre-fill from other Sprint surfaces (e.g. Continue where you left off)
  useEffect(() => {
    const onPrefill = (e) => {
      const msg = e?.detail?.message;
      if (typeof msg !== 'string' || !msg.trim()) return;
      setInput(msg);
      openPanel();
      // Focus the composer once the panel is mounted + animated in
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          try { inputRef.current.setSelectionRange(len, len); } catch (_) { /* noop */ }
        }
      }, 260);
    };
    window.addEventListener('tactixai:prefill-message', onPrefill);
    return () => window.removeEventListener('tactixai:prefill-message', onPrefill);
  }, []);

  // Auto-grow the composer up to ~5-6 lines, then scroll internally
  const INPUT_MAX_PX = 140;
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, INPUT_MAX_PX);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > INPUT_MAX_PX ? 'auto' : 'hidden';
  }, [input]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isStreaming]);

  const upsertConversation = async (sid, title) => {
    const userId = getUserId();
    try {
      await axios.post(
        `${API}/tactixai/conversations?user_id=${encodeURIComponent(userId)}`,
        { session_id: sid, title }
      );
    } catch (_) {
      // best-effort
    }
  };

  const renameChat = async (conv, newTitle) => {
    const userId = getUserId();
    try {
      await axios.patch(
        `${API}/tactixai/conversations/${conv.session_id}?user_id=${encodeURIComponent(userId)}`,
        { title: newTitle }
      );
      manualTitledSessionsRef.current.add(conv.session_id);
      // Ensure the auto-title-from-first-message path can no longer overwrite
      titledSessionsRef.current.add(conv.session_id);
      setConversations((prev) =>
        prev.map((c) =>
          c.session_id === conv.session_id ? { ...c, title: newTitle } : c
        )
      );
    } catch (_) {
      // best-effort
    }
  };

  // Compact, read-only Sprint context snapshot passed on every chat request.
  // Fetched lazily and cached briefly so we do not spam /api/sessions on every keystroke.
  const contextCacheRef = useRef({ ts: 0, snapshot: null });
  const getSprintContextSnapshot = useCallback(async () => {
    const now = Date.now();
    if (contextCacheRef.current.snapshot && now - contextCacheRef.current.ts < 30000) {
      return contextCacheRef.current.snapshot;
    }
    const userId = getUserId();
    const token = localStorage.getItem('authToken');
    try {
      const res = await axios.get(
        `${API}/sessions?user_id=${encodeURIComponent(userId)}&limit=1000&skip=0`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const sessions = Array.isArray(res.data) ? res.data : res.data?.sessions || [];
      const snapshot = buildSprintContextSnapshot(sessions);
      contextCacheRef.current = { ts: now, snapshot };
      return snapshot;
    } catch (_) {
      return null;
    }
  }, []);

  const sendMessage = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || isStreaming) return;
    if (usage && typeof usage.remaining === 'number' && usage.remaining <= 0) {
      setLimitError(`Daily TactixAI limit reached (${usage.limit}/day). Resets at midnight.`);
      return;
    }
    setLimitError(null);

    setInput('');
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: trimmed, created_at: new Date().toISOString() },
      { role: 'assistant', content: '' },
    ]);
    setIsStreaming(true);

    const sid = activeSessionId;
    const userId = getUserId();

    // If this is the first user message in this conversation, seed its title
    if (!titledSessionsRef.current.has(sid)) {
      titledSessionsRef.current.add(sid);
      const title = deriveTitle(trimmed);
      // Fire and forget; the chat streaming call also upserts updated_at on the backend
      upsertConversation(sid, title).then(() => refreshConversations());
    }

    const controller = new AbortController();
    abortRef.current = controller;

    // Attach a compact read-only Sprint context snapshot (best-effort; never blocks send)
    const sprintContext = await getSprintContextSnapshot();

    try {
      const tz = getBrowserTZ();
      const response = await fetch(
        `${API}/tactixai/chat?user_id=${encodeURIComponent(userId)}${tz ? `&tz=${encodeURIComponent(tz)}` : ''}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: trimmed,
            session_id: sid,
            sprint_context: sprintContext || undefined,
          }),
          signal: controller.signal,
        }
      );

      if (!response.ok || !response.body) {
        if (response.status === 429) {
          let payload = null;
          try { payload = await response.json(); } catch (_) { /* noop */ }
          const detail = payload?.detail || {};
          setLimitError(
            `Daily TactixAI limit reached (${detail.limit ?? usage.limit}/day). Resets at midnight.`
          );
          if (typeof detail.limit === 'number') {
            setUsage({
              date: detail.date,
              used: detail.used ?? detail.limit,
              limit: detail.limit,
              remaining: 0,
            });
          }
          // Remove the optimistic assistant placeholder + the just-added user message
          setMessages((prev) => prev.slice(0, -2));
          return;
        }
        throw new Error(`Chat request failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';

        for (const frame of frames) {
          const line = frame.trim();
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            if (evt.delta) {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.role === 'assistant') {
                  next[next.length - 1] = {
                    ...last,
                    content: (last.content || '') + evt.delta,
                  };
                }
                return next;
              });
            } else if (evt.error) {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.role === 'assistant') {
                  next[next.length - 1] = {
                    ...last,
                    content:
                      (last.content || '') +
                      `\n\n_Sorry, something went wrong: ${evt.error}_`,
                  };
                }
                return next;
              });
            }
          } catch (_) {
            // ignore parse errors mid-frame
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            next[next.length - 1] = {
              ...last,
              content: '_TactixAI is unavailable right now. Please try again._',
            };
          }
          return next;
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      // Stamp the assistant message once streaming completes so timestamp doesn't flicker
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant' && last.content && !last.created_at) {
          next[next.length - 1] = { ...last, created_at: new Date().toISOString() };
        }
        return next;
      });
      // Refresh list so updated_at ordering reflects latest activity
      refreshConversations();
      refreshUsage();
    }
  };

  const performReset = async () => {
    setPendingReset(false);
    if (isStreaming) return;
    const userId = getUserId();
    try {
      await axios.delete(
        `${API}/tactixai/history/${activeSessionId}?user_id=${encodeURIComponent(userId)}`
      );
    } catch (_) {
      // best-effort
    }
    setMessages([]);
    // Preserve manual title if any; only clear the auto-title marker for future user messages
    if (!manualTitledSessionsRef.current.has(activeSessionId)) {
      titledSessionsRef.current.delete(activeSessionId);
    }
  };

  const requestReset = () => {
    if (isStreaming || messages.length === 0) return;
    setPendingReset(true);
  };

  const createNewChat = async () => {
    if (isStreaming) return;
    const sid = newSessionId();
    persistActiveSession(sid);
    setActiveSessionId(sid);
    setMessages([]);
    setIsHistoryOpen(false);
    // Seed a conversation row so it appears in history immediately
    await upsertConversation(sid, 'New chat');
    refreshConversations();
  };

  const switchToChat = async (sid) => {
    if (isStreaming || sid === activeSessionId) {
      setIsHistoryOpen(false);
      return;
    }
    persistActiveSession(sid);
    setActiveSessionId(sid);
    setIsHistoryOpen(false);
    await loadHistory(sid);
  };

  const confirmDeleteChat = async () => {
    const target = pendingDelete;
    if (!target) return;
    const userId = getUserId();
    try {
      await axios.delete(
        `${API}/tactixai/conversations/${target.session_id}?user_id=${encodeURIComponent(userId)}`
      );
    } catch (_) {
      // best-effort
    }
    setPendingDelete(null);
    // Refresh list and, if we deleted the active chat, route to next best
    const userId2 = getUserId();
    try {
      const res = await axios.get(
        `${API}/tactixai/conversations?user_id=${encodeURIComponent(userId2)}`
      );
      const list = res.data?.conversations || [];
      setConversations(list);
      if (target.session_id === activeSessionId) {
        if (list.length > 0) {
          const nextSid = list[0].session_id;
          persistActiveSession(nextSid);
          setActiveSessionId(nextSid);
          await loadHistory(nextSid);
        } else {
          const sid = newSessionId();
          persistActiveSession(sid);
          setActiveSessionId(sid);
          setMessages([]);
        }
      }
    } catch (_) {
      // best-effort
    }
  };

  const handleUseSprint = (card) => {
    window.dispatchEvent(
      new CustomEvent('tactixai:apply-sprint-card', { detail: card })
    );
    closePanel();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const historyList = (
    <div className="flex flex-col h-full">
      <button
        type="button"
        onClick={createNewChat}
        data-testid="tactixai-new-chat-button"
        className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs font-medium px-2 py-1.5 mb-2 hover:bg-amber-500/20 transition-colors"
      >
        <Plus className="h-3.5 w-3.5" />
        New chat
      </button>
      <div
        className="flex-1 overflow-y-auto space-y-1 pr-1"
        data-testid="tactixai-history-list"
      >
        {conversations.length === 0 ? (
          <div className="text-[11px] text-slate-500 px-1 py-2 leading-relaxed">
            No conversations yet. Start a new chat to plan tonight&apos;s Sprint.
          </div>
        ) : (
          conversations.map((c) => (
            <ConversationRow
              key={c.session_id}
              conv={c}
              isActive={c.session_id === activeSessionId}
              onSelect={switchToChat}
              onRequestDelete={setPendingDelete}
              onRename={renameChat}
            />
          ))
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Persistent floating tab button */}
      {!isOpen && !isMounted && (
        <button
          type="button"
          onClick={openPanel}
          data-testid="tactixai-open-button"
          aria-label="Open TactixAI chat"
          className="fixed bottom-4 right-4 z-40 flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-orange-500/20 hover:from-amber-600 hover:to-orange-600 transition-all motion-safe:animate-in"
        >
          <Sparkles className="h-4 w-4" />
          <span className="hidden sm:inline">TactixAI</span>
        </button>
      )}

      {/* Collapsible chat panel (mounted during exit animation) */}
      {isMounted && (
        <div
          data-testid="tactixai-panel"
          data-history-open={isHistoryOpen ? 'true' : 'false'}
          data-entering={isEntering ? 'true' : 'false'}
          style={{
            transformOrigin: 'bottom right',
            transition:
              'opacity 220ms cubic-bezier(0.16, 1, 0.3, 1), transform 220ms cubic-bezier(0.16, 1, 0.3, 1)',
            opacity: isEntering ? 1 : 0,
            transform: isEntering
              ? 'translateY(0) scale(1)'
              : 'translateY(12px) scale(0.94)',
          }}
          className={`fixed z-40 bg-slate-900/95 backdrop-blur-md border border-slate-700 shadow-2xl flex
                     bottom-0 right-0 left-0 sm:left-auto
                     sm:bottom-4 sm:right-4
                     h-[75vh] sm:h-[560px]
                     ${isHistoryOpen ? 'sm:w-[560px]' : 'sm:w-[380px]'}
                     sm:rounded-xl rounded-t-xl motion-reduce:transition-none`}
        >
          {/* Desktop history rail */}
          {isHistoryOpen && (
            <div
              data-testid="tactixai-history-rail"
              className="hidden sm:flex flex-col w-[180px] shrink-0 border-r border-slate-700/70 bg-slate-950/60 p-2"
            >
              {historyList}
            </div>
          )}

          <div className="flex-1 min-w-0 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-3 border-b border-slate-700/70">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  type="button"
                  onClick={() => setIsHistoryOpen((v) => !v)}
                  data-testid="tactixai-history-toggle"
                  aria-label={isHistoryOpen ? 'Close history' : 'Open history'}
                  className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-700/60"
                  title="Chats"
                >
                  <Menu className="h-4 w-4" />
                </button>
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-amber-500 to-orange-500 shrink-0">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div className="leading-tight min-w-0">
                  <div className="text-sm font-semibold text-white">TactixAI</div>
                  <div
                    className="text-[10px] text-slate-400"
                    data-testid="tactixai-usage"
                  >
                    Focus coach
                    {typeof usage.remaining === 'number' && (
                      <>
                        <span className="mx-1 text-slate-600">·</span>
                        <span
                          className={
                            usage.remaining === 0
                              ? 'text-red-400'
                              : usage.remaining <= 3
                              ? 'text-amber-400'
                              : 'text-slate-400'
                          }
                        >
                          {usage.remaining} messages remaining today
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={requestReset}
                  disabled={isStreaming || messages.length === 0}
                  data-testid="tactixai-reset-button"
                  aria-label="Reset conversation"
                  className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-700/60 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Reset this chat"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={closePanel}
                  data-testid="tactixai-close-button"
                  aria-label="Close TactixAI"
                  className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-700/60"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="tactixai-messages"
              className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
            >
              {messages.length === 0 && !isStreaming && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Hey — I&apos;m TactixAI. Tap a starter below, or ask anything about tonight&apos;s Sprint.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {QUICK_PROMPTS.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => sendMessage(p)}
                        data-testid={`tactixai-quick-prompt-${i}`}
                        className="text-left text-xs text-slate-200 rounded-md border border-slate-700 hover:border-amber-500/60 hover:bg-slate-800/60 px-3 py-2 transition-colors leading-snug"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, idx) => {
                const isAssistant = m.role === 'assistant';
                const card = isAssistant ? parseSprintCard(m.content) : null;
                if (card) {
                  const stamp = formatMsgTime(m.created_at);
                  return (
                    <div
                      key={idx}
                      data-testid={`tactixai-msg-${idx}`}
                      data-role={m.role}
                      className="flex flex-col items-start"
                    >
                      <div className="max-w-[95%] w-full">
                        <SprintCard card={card} onUseSprint={handleUseSprint} />
                      </div>
                      {stamp && (
                        <div
                          data-testid={`tactixai-msg-time-${idx}`}
                          className="mt-1 text-[10px] text-slate-500 leading-none whitespace-nowrap"
                        >
                          {stamp}
                        </div>
                      )}
                    </div>
                  );
                }
                const stamp = formatMsgTime(m.created_at);
                const isUser = m.role === 'user';
                return (
                  <div
                    key={idx}
                    data-testid={`tactixai-msg-${idx}`}
                    data-role={m.role}
                    className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                        isUser
                          ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white whitespace-pre-wrap'
                          : 'bg-slate-800/80 text-slate-100 border border-slate-700/60'
                      }`}
                    >
                      {m.content ? (
                        !isUser ? (
                          <MarkdownLite text={m.content} />
                        ) : (
                          m.content
                        )
                      ) : (
                        <span className="inline-flex gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-pulse" />
                          <span
                            className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-pulse"
                            style={{ animationDelay: '150ms' }}
                          />
                          <span
                            className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-pulse"
                            style={{ animationDelay: '300ms' }}
                          />
                        </span>
                      )}
                    </div>
                    {stamp && m.content && (
                      <div
                        data-testid={`tactixai-msg-time-${idx}`}
                        className="mt-1 text-[10px] text-slate-500 leading-none whitespace-nowrap"
                      >
                        {stamp}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Input */}
            <div className="border-t border-slate-700/70 p-2.5">
              {limitError && (
                <div
                  data-testid="tactixai-limit-banner"
                  className="mb-2 text-[11px] leading-relaxed rounded border border-red-500/40 bg-red-950/40 text-red-200 px-2 py-1.5"
                >
                  {limitError}
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  data-testid="tactixai-input"
                  placeholder={
                    usage.remaining <= 0
                      ? `Daily limit reached — resets at midnight`
                      : 'Ask TactixAI…'
                  }
                  className="flex-1 resize-none rounded-md bg-slate-800/80 border border-slate-700 text-sm text-white placeholder-slate-500 px-3 py-2 focus:outline-none focus:border-amber-500/70 leading-relaxed disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isStreaming || usage.remaining <= 0}
                />
                <button
                  type="button"
                  onClick={() => sendMessage()}
                  disabled={isStreaming || !input.trim() || usage.remaining <= 0}
                  data-testid="tactixai-send-button"
                  aria-label="Send message"
                  className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-amber-500 to-orange-500 text-white h-9 w-9 shrink-0 hover:from-amber-600 hover:to-orange-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile history drawer (slide-over inside the panel) */}
          {isHistoryOpen && (
            <div
              data-testid="tactixai-history-drawer"
              className="sm:hidden absolute inset-0 z-10 bg-slate-950/95 backdrop-blur-md flex flex-col border-t border-slate-700 rounded-t-xl"
            >
              <div className="flex items-center justify-between px-3 py-3 border-b border-slate-700/70">
                <div className="text-sm font-semibold text-white">Chats</div>
                <button
                  type="button"
                  onClick={() => setIsHistoryOpen(false)}
                  data-testid="tactixai-history-close"
                  aria-label="Close chat history"
                  className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-700/60"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 min-h-0 p-2.5">{historyList}</div>
            </div>
          )}

          {/* Delete confirmation (branded, no browser confirm()) */}
          {pendingDelete && (
            <div
              data-testid="tactixai-delete-confirm"
              className="absolute inset-0 z-20 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center px-6 rounded-xl"
            >
              <div className="w-full max-w-[300px] rounded-lg border border-slate-700 bg-slate-900 p-4 space-y-3 shadow-2xl">
                <div className="text-sm font-medium text-white">Delete this chat?</div>
                <div className="text-xs text-slate-400 leading-relaxed">
                  This removes only the chat titled
                  <span className="text-slate-200"> &ldquo;{pendingDelete.title}&rdquo;</span>.
                  Your Sprints, Journal, and other chats stay intact.
                </div>
                <div className="flex gap-2 justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => setPendingDelete(null)}
                    data-testid="tactixai-delete-cancel"
                    className="text-xs text-slate-300 px-3 py-1.5 rounded hover:bg-slate-800/70"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteChat}
                    data-testid="tactixai-delete-confirm-button"
                    className="text-xs text-white bg-red-600 hover:bg-red-500 px-3 py-1.5 rounded font-medium"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reset confirmation (branded) */}
          {pendingReset && (
            <div
              data-testid="tactixai-reset-confirm"
              className="absolute inset-0 z-20 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center px-6 rounded-xl"
            >
              <div className="w-full max-w-[300px] rounded-lg border border-slate-700 bg-slate-900 p-4 space-y-3 shadow-2xl">
                <div className="text-sm font-medium text-white">Reset this chat?</div>
                <div className="text-xs text-slate-400 leading-relaxed">
                  This will clear the messages in this conversation.
                </div>
                <div className="flex gap-2 justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => setPendingReset(false)}
                    data-testid="tactixai-reset-cancel"
                    className="text-xs text-slate-300 px-3 py-1.5 rounded hover:bg-slate-800/70"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={performReset}
                    data-testid="tactixai-reset-confirm-button"
                    className="text-xs text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 px-3 py-1.5 rounded font-medium"
                  >
                    Reset chat
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TactixAIChat;
